import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import API_BASE_URL from "../config/api";

function PopularDestination() {
  const containerRef = useRef(null);
  const [destinations, setDestinations] = useState([]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [activeIndex, setActiveIndex] = useState(null);
  const navigate = useNavigate();

  // Track window resize for responsiveness
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const isMobile = windowWidth < 768;
  const isTablet = windowWidth >= 768 && windowWidth < 1024;

  // Fetch destinations and packages to filter out empty ones
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [destRes, pkgRes] = await Promise.all([
          fetch(`${API_BASE_URL}/destinations`),
          fetch(`${API_BASE_URL}/packages`)
        ]); // Fetch both in parallel

        const destinationsData = await destRes.json();
        const packagesData = await pkgRes.json();

        // 1. Extract all valid destination IDs from active packages
        const validDestinationIds = new Set();

        packagesData.forEach(pkg => {
          // Check if package is active (optional, but good practice if you have isActive flag)
          // parsing logic as per schema structure: package -> itinerary -> destinations -> destinationId
          if (pkg.itinerary && Array.isArray(pkg.itinerary)) {
            pkg.itinerary.forEach(day => {
              if (day.destinations && Array.isArray(day.destinations)) {
                day.destinations.forEach(dest => {
                  // destinationId is populated, so might be an object or id string
                  const id = dest.destinationId?._id || dest.destinationId;
                  if (id) validDestinationIds.add(String(id));
                });
              }
            });
          }
        });

        // 2. Filter destinations that are active AND have at least one package
        const activeDestinations = destinationsData.filter(d =>
          d.isActive !== false && validDestinationIds.has(String(d._id))
        );

        setDestinations(activeDestinations);
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };
    fetchData();
  }, []);

  // Add smooth scroll behavior to container
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.style.scrollBehavior = "smooth";
    }
  }, []);

  // Responsive card size calculation
  const getImageStyle = (index) => {
    // For desktop, use fixed values; for mobile, calculate based on window width.
    // Desktop reference:
    //   Active card: width 280px, height 400px.
    //   Base card: width 180px, height 300px.
    const activeWidthDesktop = 280;
    const activeHeightDesktop = 400;
    const baseWidthDesktop = 180;
    const baseHeightDesktop = 300;

    // Determine active card width (for spacing)
    const cardWidth = isMobile ? windowWidth * 0.8 : activeWidthDesktop;

    // Calculate responsive sizes preserving desktop ratios:
    const activeSize = {
      width: isMobile ? `${cardWidth}px` : `${activeWidthDesktop}px`,
      height: isMobile
        ? `${cardWidth * (activeHeightDesktop / activeWidthDesktop)}px`
        : `${activeHeightDesktop}px`,
      opacity: 1,
    };
    const baseSize = {
      width: isMobile
        ? `${cardWidth * (baseWidthDesktop / activeWidthDesktop)}px`
        : `${baseWidthDesktop}px`,
      height: isMobile
        ? `${cardWidth * (baseHeightDesktop / activeWidthDesktop)}px`
        : `${baseHeightDesktop}px`,
      opacity: 1,
    };

    // Determine if this card should be active based on hover/touch
    const isActive = activeIndex === index;

    return {
      ...(isActive ? activeSize : baseSize),
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      borderRadius: "12px",
      boxShadow: isActive ? "0 12px 30px rgba(0,0,0,0.2)" : "0 8px 24px rgba(0,0,0,0.1)",
    };
  };

  const handleDestinationClick = (destinationName) => {
    navigate(`/DestinationPackages?country=${destinationName}`);
  };

  // Responsive container for the image slider
  const responsiveContainerStyle = {
    ...containerStyle,
    padding: isMobile ? "0 10px" : containerStyle.padding,
  };

  // Responsive gap for the image container
  const responsiveImageContainerStyle = {
    ...imageContainerStyle,
    gap: isMobile ? "10px" : "20px",
  };

  // Handle mouse enter/leave and touch for correct image activation
  const handleMouseEnter = (index) => {
    if (!isMobile) {
      setActiveIndex(index);
    }
  };

  const handleMouseLeave = () => {
    if (!isMobile) {
      setActiveIndex(null);
    }
  };

  const handleTouchStart = (index) => {
    if (isMobile) {
      setActiveIndex(index);
    }
  };

  return (
    <div style={responsiveContainerStyle}>
      <div style={headerStyle}>
        <h2 style={titleStyle}>Popular Destinations</h2>
        <div style={dividerStyle} />
      </div>

      <div style={responsiveImageContainerStyle} ref={containerRef}>
        {destinations.map((destination, index) => (
          <div
            key={index}
            style={cardStyle}
            onClick={() => handleDestinationClick(destination.name)}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onTouchStart={() => handleTouchStart(index)}
          >
            <div style={{ position: "relative", ...getImageStyle(index) }}>
              <img
                src={destination.images[0]}
                alt={destination.name}
                style={imageStyle}
              />
              <div style={gradientOverlayStyle} />
              <div style={nameStyle}>
                {destination.name}
                <div style={{
                  ...exploreStyle,
                  opacity: 1,
                  transform: activeIndex === index ? "translateX(5px)" : "translateX(0)"
                }}>
                  Explore Now →
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Base Styles (inline)
const containerStyle = {
  maxWidth: "1440px",
  margin: "40px auto",
  padding: "0 20px",
  position: "relative"
};

const headerStyle = {
  textAlign: "center",
  marginBottom: "40px"
};

const titleStyle = {
  fontFamily: "MonteCarlo, sans-serif",
  fontSize: "40px",
  fontWeight: "400",
  color: "#1CA8CB"
};

const dividerStyle = {
  width: "80px",
  height: "4px",
  backgroundColor: "#1CA8CB",
  margin: "0 auto",
  borderRadius: "2px"
};

const imageContainerStyle = {
  display: "flex",
  overflowX: "auto",
  scrollSnapType: "x mandatory", // Changed to mandatory for better snapping
  padding: "20px 0",
  gap: "20px", // Base gap between items
  // Hide scrollbar on most browsers
  scrollbarWidth: "none",
  msOverflowStyle: "none",
  // Add this to hide webkit scrollbars
  WebkitOverflowScrolling: "touch",
  "&::-webkit-scrollbar": {
    display: "none",
  }
};

const cardStyle = {
  flex: "0 0 auto",
  scrollSnapAlign: "center",
  position: "relative",
  cursor: "pointer",
};

const imageStyle = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  borderRadius: "12px"
};

const gradientOverlayStyle = {
  position: "absolute",
  bottom: 0,
  left: 0,
  right: 0,
  height: "50%",
  background: "linear-gradient(transparent, rgba(0,0,0,0.7))",
  borderRadius: "12px"
};

const nameStyle = {
  position: "absolute",
  bottom: "20px",
  left: "20px",
  color: "white",
  fontSize: "clamp(16px, 2vw, 24px)",
  fontWeight: 600,
  textAlign: "left",
  fontFamily: "'Poppins', sans-serif",
  textShadow: "0 2px 4px rgba(0,0,0,0.3)"
};

const exploreStyle = {
  fontSize: "clamp(12px, 1.2vw, 16px)",
  fontWeight: 400,
  marginTop: "4px",
  transition: "all 0.3s ease"
};

export default PopularDestination;